import { useMemo } from 'react';

export interface AnchorRect { x: number; y: number; w: number; h: number };

export const useAnchoredPosition = (
  anchor: AnchorRect | null,
  options?: { width?: number; gutter?: number; maxHeight?: number; align?: 'start' | 'center' | 'end'; estimatedHeight?: number }
) => {
  return useMemo(() => {
    if (!anchor) return { top: 0, left: 0, maxHeight: 0 };
    const panelWidth = options?.width ?? 240;
    const gutter = options?.gutter ?? 12;
    const viewportW = typeof global !== 'undefined' && (global as any).window?.innerWidth ? (global as any).window.innerWidth : 1024;
    const viewportH = typeof global !== 'undefined' && (global as any).window?.innerHeight ? (global as any).window.innerHeight : 768;

    // Prefer placing below the anchor
    const desiredTop = anchor.y + anchor.h + 6;
    const availableBelow = viewportH - desiredTop - gutter;
    const computedMax = options?.maxHeight ?? 320;
    const maxHeight = Math.min(computedMax, Math.max(180, availableBelow));

    // Horizontal: preferred alignment then clamp
    const align = options?.align ?? 'start';
    let preferredLeft = anchor.x;
    if (align === 'center') {
      preferredLeft = anchor.x + (anchor.w - panelWidth) / 2;
    } else if (align === 'end') {
      preferredLeft = anchor.x + anchor.w - panelWidth;
    }
    const maxLeft = viewportW - panelWidth - gutter;
    let left = Math.max(gutter, Math.min(preferredLeft, maxLeft));

    // Vertical: if not enough space below, place above
    // When flipping above, prefer a more accurate panel height if provided to avoid
    // overshooting far above the anchor (common for short tooltips near viewport bottom)
    const estimatedHeight = options?.estimatedHeight;
    const heightForFlip = Math.min(computedMax, Math.max(estimatedHeight ?? maxHeight, 60));
    const top = availableBelow < 180 ? Math.max(gutter, anchor.y - heightForFlip - 6) : desiredTop;

    return { top, left, maxHeight };
  }, [anchor, options?.width, options?.gutter, options?.maxHeight, options?.align, options?.estimatedHeight]);
};


